import React, { useState, useEffect } from 'react';
import {
  Typography,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Backdrop,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams } from 'react-router-dom';
import { intakeFormService } from '../services/intakeForm.service';
import DynamicFieldLoader from '../components/intake/DynamicFieldLoader';
import api from '../services/api';

const IntakeForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResponse, setSubmitResponse] = useState<any | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [intakeFormInfo, setIntakeFormInfo] = useState<any>(null);

  useEffect(() => {
    if (token) {
      loadFormData();
    }
  }, [token]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      const response = await intakeFormService.getByToken(token!);
      setFormData(response.form_data || {});
      setIntakeFormInfo(response);
    } catch (err) {
      setError(
        'Failed to load intake form. Please check the URL and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormDataChange = async (newFormData: any) => {
    setFormData(newFormData);

    // Auto-save the form data
    try {
      setSaving(true);
      await intakeFormService.updateByToken(token!, newFormData);
    } catch (err) {
      console.error('Failed to save form data:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await intakeFormService.submit(token!);
      setSubmitResponse(response);
      setShowSuccessDialog(true);
      setError(null);
    } catch (err: any) {
      setError('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    window.location.reload();
  };

  const handleDownloadPdf = async () => {
    if (!submitResponse?.download_url) return;
    try {
      setDownloading(true);
      const urlParts = submitResponse.download_url.split('/');
      const caseId = urlParts[urlParts.length - 2];
      const response = await api.get(`/cases/${caseId}/download-intake-form-pdf`, {
        responseType: 'blob',
        timeout: 30000,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = submitResponse.pdf_filename || 'intake_form.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !intakeFormInfo) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 3 }}>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────────
  const serviceTypeId  = intakeFormInfo?.case?.service_type_id;
  const intakeFormTypeId = intakeFormInfo?.intake_form_type_id;
  const formTitle      = intakeFormInfo?.intake_form_type?.name || 'Vocational Assessment Form';
  const currentSection = formData?.current_section || null;
  const isPdfGenerated = Boolean(intakeFormInfo?.pdf_file_path);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>

      {/* ── Submitting loader overlay ── */}
      <Backdrop open={submitting} sx={{ zIndex: theme => theme.zIndex.modal + 10, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.65)' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: 'white' }} />
        <Typography variant='h6' color='white' fontWeight={600}>
          Submitting your form…
        </Typography>
        <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.75)' }}>
          Please wait, do not close this page.
        </Typography>
      </Backdrop>

      {/* ── Success dialog ── */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessClose}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, textAlign: 'center', overflow: 'visible' } }}
      >
        <DialogContent sx={{ pt: 5, pb: 4, px: 4 }}>
          {/* Big green check */}
          <Box
            sx={{
              width: 80, height: 80, borderRadius: '50%',
              bgcolor: 'success.main', display: 'flex', alignItems: 'center',
              justifyContent: 'center', mx: 'auto', mb: 3,
              boxShadow: '0 4px 20px rgba(46,125,50,0.35)',
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'white' }} />
          </Box>

          <Typography variant='h5' fontWeight={700} gutterBottom>
            Form Submitted!
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            {submitResponse?.message || 'Your intake form has been submitted successfully.'}
          </Typography>

          {submitResponse?.pdf_generated ? (
            <Box sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light', borderRadius: 2, p: 2, mb: 3, textAlign: 'left' }}>
              <Typography variant='body2' fontWeight={600} color='success.dark' gutterBottom>
                📄 PDF Generated
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {submitResponse.pdf_filename}
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size='small'
                  variant='outlined'
                  color='success'
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                >
                  {downloading ? 'Downloading…' : '📥 Download PDF'}
                </Button>
              </Box>
            </Box>
          ) : submitResponse?.error ? (
            <Alert severity='warning' sx={{ mb: 3, textAlign: 'left' }}>
              PDF generation failed: {submitResponse.error}
            </Alert>
          ) : null}

          <Button
            variant='contained'
            color='success'
            fullWidth
            size='large'
            onClick={handleSuccessClose}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            OK — Close
          </Button>
        </DialogContent>
      </Dialog>
      {/* Top brand bar */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant='subtitle1' fontWeight={700} sx={{ letterSpacing: 0.3 }}>
          {formTitle}
        </Typography>
        {isPdfGenerated && (
          <Box sx={{ ml: 'auto', bgcolor: 'warning.main', color: 'warning.contrastText', borderRadius: 1, px: 1.5, py: 0.3 }}>
            <Typography variant='caption' fontWeight={600}>Read-only</Typography>
          </Box>
        )}
      </Box>

      {isPdfGenerated && (
        <Alert severity='warning' sx={{ borderRadius: 0 }}>
          This form has already been submitted and a PDF generated. No changes can be made.
        </Alert>
      )}

      {/* Wizard body */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {serviceTypeId ? (
          <DynamicFieldLoader
            serviceTypeId={serviceTypeId}
            intakeFormTypeId={intakeFormTypeId}
            formData={formData}
            onFormDataChange={isPdfGenerated ? () => undefined : handleFormDataChange}
            showAllSections={true}
            onSubmit={isPdfGenerated ? undefined : handleSubmit}
            initialSection={currentSection}
            readOnly={isPdfGenerated}
            saving={saving}
          />
        ) : (
          <Box sx={{ p: 4 }}>
            <Alert severity='warning'>No service type information available. Please contact support.</Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default IntakeForm;
